/**
 * LookupRenameSlugForm — per-form Save sub-form for renaming a slug
 * (Block 6.C.1 / ADR-024 D-affiliation-editor-shape three-criterion).
 *
 * Per-form Save earns its place per the three-criterion principle:
 *   1. Multi-field coherence — new_slug + cascade preview ("affects N
 *      records") form one semantic edit.
 *   2. Inter-field validation — new_slug must pass slugify shape AND
 *      not collide with existing slugs of the same type.
 *   3. Undo-stack-as-one-logical-unit — undo means restore old slug
 *      atomically, not character-by-character.
 *
 * Per ADR-018: slug rename is a single-row UPDATE; uuid FK columns
 * continue referencing the same row id (no cascade write needed). The
 * "affects N records" preview is informational, not load-bearing for the
 * write — it surfaces blast radius so the operator can confirm intent.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LookupTypeUrl } from '@/lib/types';
import { lookupsApi } from '@/lib/lookups';
import { formatRecordCount } from '@/lib/format';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { refreshFamily } from '@/lib/refreshFamily';

export interface LookupRenameSlugFormProps {
  tenantSlug: string;
  type: LookupTypeUrl;
  rowId: string;
  currentSlug: string;
  recordCount: number;
  invalidationKey: ReadonlyArray<unknown>;
  onClose: () => void;
}

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export function LookupRenameSlugForm({
  tenantSlug,
  type,
  rowId,
  currentSlug,
  recordCount,
  invalidationKey,
  onClose,
}: LookupRenameSlugFormProps): ReactNode {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(currentSlug);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  // The new slug, the type and the list to refresh (the family) are read
  // at the press and travel as the variables.
  const mutation = useMutation({
    mutationFn: async (v: { input: { new_slug: string }; type: typeof type; family: readonly unknown[] }) => {
      if (v.type === 'subcategories') {
        return lookupsApi.renameSubcategorySlug(tenantSlug, rowId, v.input);
      }
      if (v.type === 'specialties') {
        return lookupsApi.renameSpecialtySlug(tenantSlug, rowId, v.input);
      }
      if (v.type === 'highlights') {
        return lookupsApi.renameHighlightSlug(tenantSlug, rowId, v.input);
      }
      return lookupsApi.renameServiceAreaSlug(tenantSlug, rowId, v.input);
    },
    onSuccess: async (_res, v) => {
      await queryClient.invalidateQueries({ queryKey: v.family });
      onClose();
    },
    onError: (e: Error & { status?: number; body?: { error?: string; slug?: string } }, v) => {
      if (e.status === 409 && e.body?.error === 'lookup_slug_conflict') {
        setError(`The short name "${e.body.slug ?? v.input.new_slug}" is already used. Choose a different one.`);
      } else {
        setError(pageWriteText(e, contact));
      }
    },
  });

  function handleSave(): void {
    setError(null);
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setError('Enter a short name.');
      return;
    }
    if (!SLUG_PATTERN.test(trimmed)) {
      setError('Use only lowercase letters, numbers and hyphens, with no spaces.');
      return;
    }
    if (trimmed === currentSlug) {
      onClose();
      return;
    }
    mutation.mutate({ input: { new_slug: trimmed }, type, family: refreshFamily(invalidationKey) });
  }

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md p-3 mt-2">
      <div className="text-sm font-semibold mb-2">Rename slug</div>
      <div className="text-xs text-ink-soft mb-2">
        Current: <span className="font-mono">{currentSlug}</span>
      </div>
      <label className="block text-xs text-ink-soft mb-1">New slug</label>
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        aria-label="New slug"
        disabled={mutation.isPending}
        className="w-full min-h-11 px-3 py-2 mb-2 border border-sage-400/30 rounded-md text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      />
      <div className="text-xs text-ink-soft mb-2">
        Affects {formatRecordCount(recordCount)} record{recordCount === 1 ? '' : 's'}.
        FK references continue working; only the slug label changes.
      </div>
      {error !== null && (
        <div role="alert" className="text-xs text-red-700 mb-2" data-testid="lookup-rename-slug-error">
          ⚠ {error}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="min-h-11 px-3 py-1 text-sm text-ink-soft hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending || draft.trim().length === 0}
          className="min-h-11 px-3 py-1 text-sm bg-amber-600 text-cream rounded-md hover:bg-amber-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          {mutation.isPending ? 'Renaming…' : 'Rename slug'}
        </button>
      </div>
    </div>
  );
}
